import React, { ForwardedRef, forwardRef, useImperativeHandle, useRef, useState } from "react"
import { ModalHandle, ToastHandle, Toast, Modal, IFile } from "ui"
import { Eye, XMark, Svg } from "ui/icons"
import { ReactChildren } from "ui/types"
import { FileDisplay } from "./FileDisplay"
import { FileInfo } from "./FileInfo"

export enum ESideInfoType {
    None,
    Left,
    Right
}

interface FileViewerProps {
    sidebar?: ESideInfoType
    sidebarTop?: ReactChildren
    sidebarBottom?: ReactChildren
}

interface FileViewerHandle {
    view: (file: IFile) => void,
}

export function FileViewerComponent(
    {
        sidebar = ESideInfoType.None,
        sidebarTop,
        sidebarBottom,
    }: FileViewerProps,
    ref: ForwardedRef<FileViewerHandle>
) {

    const modal = useRef<ModalHandle>(null)
    const refToast = useRef<ToastHandle>(null)
    const [file, setFile] = useState<IFile>()

    useImperativeHandle(ref, () => ({
        view: (file: IFile) => {
            modal.current?.open()
            setFile(file)
        }
    }))

    const close = () => {
        modal.current?.close()
        setFile(undefined)
    }

    const contentByType = () => {
        switch(file?.extension) {
            case 'pdf':
                return <embed src={file.content}  type="application/pdf" className="h-full w-full"></embed>
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
            case 'webp':
            case 'svg':
            case 'bmp':
            case 'tiff':
                return <img src={file.content} className="max-h-full maw-w-full" />
        }
    }

    return (
        <>
            <Toast ref={refToast} />
            <Modal className="flex-col lg:w-[95%] h-[95%] w-full" ref={modal}>
                {/* Refonte « Aurora » : en-tête de visionneuse — badge document iris, titre, bouton fermer rond */}
                <div className="shrink-0 bg-white/90 dark:bg-color9 backdrop-blur text-ink dark:text-white font-semibold text-[15px] tracking-[-0.01em] pl-4 pr-3 py-3 flex items-center gap-3 border-b border-line/80 dark:border-color7">
                    <span className="grid place-items-center w-9 h-9 shrink-0 rounded-xl bg-gradient-to-br from-brand-600 to-violet-500 text-white shadow-glow">
                        <Eye className="w-[18px] h-[18px]" />
                    </span>
                    <div className="flex-1 min-w-0 text-ellipsis whitespace-nowrap overflow-hidden">
                        {file?.name ?? "Fichier"}
                    </div>
                    <div onClick={close} className="cursor-pointer grid place-items-center w-9 h-9 rounded-full text-slate-400 hover:text-ink hover:bg-ink/[0.05] dark:hover:bg-color7 dark:hover:text-white transition-colors">
                        <XMark className="w-5 h-5" />
                    </div>
                </div>
                <div className="flex-1 flex">
                    <div className="flex-1 flex items-center justify-center overflow-auto">
                        {
                            file ?
                                contentByType()
                            :
                                <Svg viewBox="0 0 24 24" className={'animate-spin h-14 w-14 text-brand-500'}>
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </Svg>
                        }
                    </div>
                    <FileDisplay file={file} />
                    {
                        sidebar ?
                            <div className="w-80 flex flex-col gap-4 p-5 bg-gradient-to-b from-brand-50/60 to-slate-50/60 dark:from-color7/20 dark:to-color7/20 border-l border-line/80 dark:border-color7">
                                {sidebarTop}
                                <FileInfo file={file} />
                                {sidebarBottom}
                            </div>
                        : null
                    }
                </div>
            </Modal>
        </>
    )
}

const FileViewer = forwardRef<FileViewerHandle, FileViewerProps>(FileViewerComponent)
FileViewer.displayName = "FileViewer"

export { FileViewer }